/**
 * `multi_select` primitive for the Monitor wizard.
 *
 * Renders a list of checkboxes whose options come from one of two sources:
 *   1. Static `field.options` — declared inline on the capability schema.
 *   2. Async via `field.options_provider.script` — fetched from the
 *      POST /v1/admin/import-capabilities/{skill}/{cap}/options endpoint
 *      (Phase 2 backend, already on staging). This is what Intercom's
 *      `team_ids` uses; the resolver shells out to `scripts/intercom.sh teams`.
 *
 * Backend-tool providers (`options_provider.backend_tool`) get the same
 * loading path here, but the API itself currently returns 501 for those
 * (Phase 5 wires the tools) — surfaced as an inline error.
 */
import React from 'react';

import { Checkbox } from '../../../../ui/checkbox';
import { Label } from '../../../../ui/label';
import { useCapabilityOptions } from '../../../../../hooks/useImportCapabilities';
import type {
  CapabilityField,
  ImportCapabilityOption,
} from '../../../../../types/importCapabilities';

interface MultiSelectFieldProps {
  field: CapabilityField;
  /** Selected values keyed by `option.value`. Persisted on wizard state. */
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  /** Identifiers needed to call the async resolver — passed down from the
   *  enclosing step so this primitive doesn't need to know about wizard state. */
  skillName: string;
  capabilityId: string;
  /** Partial wizard state forwarded as `context` to the resolver. Lets
   *  future capabilities resolve options that depend on prior selections. */
  context: Record<string, unknown>;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  field,
  value,
  onChange,
  skillName,
  capabilityId,
  context,
}) => {
  const selected = value ?? [];
  const provider = field.options_provider;
  const usesAsync = Boolean(provider?.script || provider?.backend_tool);

  // useQuery (not useMutation) so React handles the mount lifecycle correctly —
  // useMutation got stuck pending under React 18 StrictMode's double-effect-fire.
  // staleTime + cached queryKey means we don't re-fetch on every render.
  const resolveQuery = useCapabilityOptions({
    skill_name: skillName,
    capability_id: capabilityId,
    field_id: field.id,
    context,
    // Scope the cache key to only the context entries this resolver
    // declares as deps; without depends_on the hook keys on the whole
    // context (current behaviour for Intercom's teams resolver, which
    // doesn't consume any sibling field).
    depends_on: provider?.depends_on,
    enabled: usesAsync,
  });

  const asyncOptions: ImportCapabilityOption[] = resolveQuery.data?.options ?? [];
  const options: ImportCapabilityOption[] = usesAsync
    ? asyncOptions
    : field.options ?? [];

  const toggle = (optionValue: string) => {
    if (selected.includes(optionValue)) {
      onChange(selected.filter((v) => v !== optionValue));
    } else {
      onChange([...selected, optionValue]);
    }
  };

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-slate-900">
        {field.label || field.id}
        {field.required ? <span className="ml-1 text-red-500">*</span> : null}
      </legend>
      {field.description ? (
        <p className="text-xs text-slate-500">{field.description}</p>
      ) : null}

      {usesAsync && resolveQuery.isPending ? (
        <p className="text-xs text-slate-500">Loading {field.label || field.id}…</p>
      ) : null}

      {usesAsync && resolveQuery.isError ? (
        <p className="text-xs text-red-600">
          Could not load {field.label || field.id}:{' '}
          {resolveQuery.error instanceof Error
            ? resolveQuery.error.message
            : 'unknown error'}
        </p>
      ) : null}

      {!resolveQuery.isPending && !resolveQuery.isError && options.length === 0 ? (
        <p className="text-xs text-slate-500">
          {!field.required
            ? `No ${(field.label || field.id).toLowerCase()} configured at the source — all will be included.`
            : 'No options available.'}
        </p>
      ) : null}

      <div className="space-y-1.5">
        {options.map((opt) => {
          const id = `field-${field.id}-${opt.value}`;
          return (
            <div key={opt.value} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
              />
              <Label htmlFor={id} className="text-sm font-normal text-slate-700">
                {opt.label}
              </Label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};

export default MultiSelectField;
